import React, { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import FormHeader from "@/components/FormHeader"
import Modal from "@/components/Modal"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import api from "@/lib/axios"
import { DataSertifikatTteSumber } from "@/types"
import { AxiosError } from "axios"
import { Loader2, MoreHorizontal } from "lucide-react"
import toast from "react-hot-toast"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { columns } from "./detail/partial/Columns"
import { DataTable } from "./detail/partial/DataTable"

const postDataAjukanTTE = async ({
  id_sumber,
}: {
  id_sumber: string | number
}) => {
  const response = await api.post("/penerimaan/sertifikat-cbt/ajukan-tte", {
    id_sumber,
  })
  return response.data
}

const batalkanPengajuanTTE = async ({
  id_sumber,
}: {
  id_sumber: string | number
}) => {
  const response = await api.get(
    "/penerimaan/sertifikat-cbt/hapus-pengajuan-tte",
    {
      params: { id_sumber },
    }
  )
  return response.data
}

const postDataProsesTTE = async ({
  id_sumber,
  pass_tte,
}: {
  id_sumber: string | number
  pass_tte: string
}) => {
  const response = await api.post("/penerimaan/sertifikat-cbt/proses-tte", {
    id_sumber,
    pass_tte,
  })
  return response.data
}

export default function Actions({ row }: { row: DataSertifikatTteSumber }) {
  const [showDetail, setShowDetail] = useState(false)
  const [showAjukan, setShowAjukan] = useState(false)
  const [showBatalAjukan, setShowBatalAjukan] = useState(false)
  const [showProsesTte, setShowProsesTte] = useState(false)
  const [form, setForm] = useState({
    password_tte: "",
  })

  const [selectedRow, setSelectedRow] =
    useState<DataSertifikatTteSumber | null>(null)

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postDataAjukanTTE,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSertifikatTteSumber"] })
      setShowAjukan(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })
  const { mutate: mutateBatalPengajuan, isPending: isPendingBatalPengajuan } =
    useMutation({
      mutationFn: batalkanPengajuanTTE,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["listSertifikatTteSumber"] })
        setShowBatalAjukan(false)
        toast.success(data?.message || "Success.")
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred."
        )
      },
    })
  const { mutate: mutateProsesTte, isPending: isPendingProsesTte } =
    useMutation({
      mutationFn: postDataProsesTTE,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["listSertifikatTteSumber"] })
        setShowBatalAjukan(false)
        toast.success(data?.message || "Success.")
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred."
        )
      },
    })

  const handleAjukanTTE = () => {
    mutate({ id_sumber: selectedRow?.id_sumber || "" })
  }
  const handleBatalAjukanTTE = () => {
    mutateBatalPengajuan({ id_sumber: selectedRow?.id_sumber || "" })
  }
  const handleProsesTTE = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    mutateProsesTte({
      id_sumber: selectedRow?.id_sumber || "",
      pass_tte: form.password_tte,
    })
  }

  return (
    <React.Fragment>
      <div className="flex items-center gap-x-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-8 h-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setShowDetail(true)
                setSelectedRow(row)
              }}
            >
              Lihat Detail
            </DropdownMenuItem>
            {row.status_tte == 1 && (
              <DropdownMenuItem
                onClick={() => {
                  setShowAjukan(true)
                  setSelectedRow(row)
                }}
              >
                Ajukan TTE
              </DropdownMenuItem>
            )}
            {row.status_tte == 2 && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    setShowBatalAjukan(true)
                    setSelectedRow(row)
                  }}
                >
                  Batalkan Pengajuan
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setShowProsesTte(true)
                    setSelectedRow(row)
                  }}
                >
                  Proses TTE
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Modal
        show={showDetail}
        onClose={() => setShowDetail(false)}
        className="md:w-[48vw] w-[95vw]"
      >
        <DataTable
          data={selectedRow?.data_berkas_sertifikat || []}
          columns={columns}
        />
        {/* <Card className="p-3">
          <table className="w-full">
            <thead>
              <tr>
                <th className="py-1 px-3 border rounded-xl">No</th>
                <th className="py-1 px-3 border rounded-xl whitespace-nowrap">
                  No Pendaftaran
                </th>
                <th className="py-1 px-3 border rounded-xl">Nama</th>
                <th className="py-1 px-3 border rounded-xl">Status TTE</th>
                <th className="py-1 px-3 border rounded-xl">File Berkas</th>
                <th className="py-1 px-3 border rounded-xl">File Berkas TTE</th>
              </tr>
            </thead>
            <tbody>
              {selectedRow?.data_berkas_sertifikat?.map(
                (berkas: DataSertifikatTte, index: number) => {
                  return (
                    <tr key={index}>
                      <td className="py-1 px-3 border">{index + 1}</td>
                      <td className="py-1 px-3 border">
                        {berkas?.data_pendaftaran?.nomor_pendaftaran}
                      </td>
                      <td className="py-1 px-3 border">
                        {berkas?.data_pendaftaran?.data_siswa?.nama_lengkap}
                      </td>
                      <td className="py-1 px-3 border">
                        <div className="flex justify-center">
                          {berkas?.status_tte_file == 0 && (
                            <Badge
                              variant={"secondary"}
                              className="whitespace-nowrap"
                            >
                              Belum di TTE
                            </Badge>
                          )}
                          {berkas?.status_tte_file == 1 && (
                            <Badge
                              variant={"success"}
                              className="whitespace-nowrap"
                            >
                              Sudah di TTE
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-3 border">
                        <Button
                          variant={"outline"}
                          className="w-full"
                          onClick={() => window.open(berkas?.url_local_file)}
                        >
                          Download Berkas
                        </Button>
                      </td>
                      <td className="py-1 px-3 border">
                        <Button
                          className="w-full"
                          disabled={berkas?.status_tte_file == 0}
                          onClick={() => window.open(berkas?.url_tte_file)}
                        >
                          Download Berkas TTE
                        </Button>
                      </td>
                    </tr>
                  )
                }
              )}
            </tbody>
          </table>
        </Card> */}
      </Modal>
      <Modal show={showAjukan} onClose={() => setShowAjukan(false)}>
        <FormHeader
          title="Form Pengajuan TTE"
          icon={<></>}
          description="Apakah anda yakin akan mengajukan TTE pada berkas ini?"
        />
        <div className="mt-5 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => setShowAjukan(false)}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            className="flex items-center gap-3"
            disabled={isPending}
            onClick={handleAjukanTTE}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Ajukan"
            )}
          </Button>
        </div>
      </Modal>

      <Modal show={showBatalAjukan} onClose={() => setShowBatalAjukan(false)}>
        <FormHeader
          title="Batalkan Pengajuan TTE"
          icon={<></>}
          description="Apakah anda yakin akan membatalkan TTE yang sudah diajukan pada berkas ini?"
        />
        <div className="mt-5 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPendingBatalPengajuan}
            onClick={() => setShowBatalAjukan(false)}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            className="flex items-center gap-3"
            disabled={isPendingBatalPengajuan}
            onClick={handleBatalAjukanTTE}
          >
            {isPendingBatalPengajuan ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Batalkan Pengajuan"
            )}
          </Button>
        </div>
      </Modal>

      <Modal show={showProsesTte} onClose={() => setShowProsesTte(false)}>
        <form onSubmit={handleProsesTTE}>
          <FormHeader
            title="Proses TTE"
            icon={<></>}
            description="Apakah anda yakin akan memproses TTE yang sudah diajukan pada berkas ini?"
          />
          <div>
            <Label>Password TTE</Label>
            <Input
              required
              type="password"
              placeholder="Ketik password tte..."
              value={form.password_tte}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  password_tte: event.target.value,
                }))
              }
            />
          </div>
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingProsesTte}
              onClick={() => setShowProsesTte(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingProsesTte}
            >
              {isPendingProsesTte ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Ya, Proses TTE"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </React.Fragment>
  )
}
